/*
 * Produktinformationen V2 — Variantenbild samt Dampfposition und die
 * Variantenauswahl selbst.
 *
 * Lag als zwei Inline-Bloecke (186 Zeilen, 7,3 KB) im Section-Markup: bei jedem
 * Seitenaufruf neu uebertragen und nie aus dem Cache. Die Logik brauchte aus
 * Liquid nur vier Dinge, die jetzt als data-Attribute am Section-Element
 * stehen: die Formular-ID und die vier Beschriftungen.
 *
 * Warum die Auswahllogik hier ueberhaupt nachgebaut ist: Dawns Logik sitzt auf
 * <product-info> aus der Section `main-product`, und die ist auf diesen Seiten
 * abgeschaltet. Ohne diesen Ersatz aenderte die Auswahl weder das Feld `id`
 * noch Preis oder Verfuegbarkeit — in den Warenkorb ging immer die erste
 * Variante.
 */
(() => {
  /* ------------------------------------- Variantenbild und Dampfposition */

  const initVariantMedia = (sectionEl) => {
    const variantJsonEl = sectionEl.querySelector('[data-produktinformationen-variants]');
    if (!variantJsonEl) return;

    let variantMap = {};
    try {
      variantMap = JSON.parse(variantJsonEl.textContent);
    } catch (error) {
      console.warn('ProduktinformationenV2 Variant-Map konnte nicht gelesen werden.', error);
      return;
    }

    const productFormId = sectionEl.dataset.produktinformationenFormId;

    const updateVariantData = (variantId) => {
      if (!variantId) variantId = document.getElementById(productFormId)?.querySelector('[name="id"]')?.value;
      if (!variantId) return;

      const variantData = variantMap[String(variantId)];
      if (!variantData) return;

      const imageEl = sectionEl.querySelector('[data-produktinformationen-variant-image]');
      const mediaEl = sectionEl.querySelector('[data-produktinformationen-media]');
      if (mediaEl) mediaEl.hidden = !variantData.image;
      if (imageEl) imageEl.hidden = !variantData.image;
      if (imageEl && variantData.image) {
        imageEl.setAttribute('src', variantData.image);
        imageEl.removeAttribute('srcset');
        imageEl.alt = variantData.alt || '';
      }

      const steamEl = sectionEl.querySelector('.produktinformationen-v2__steam');
      const steamData = variantData.steam;

      if (steamEl && steamData) {
        const clampPercent = (value, fallback) =>
          Number.isFinite(value) ? Math.min(100, Math.max(0, value)) : fallback;

        sectionEl.style.setProperty(
          '--produktinfo-steam-x-mobile',
          `${clampPercent(Number(steamData.xMobile), 50)}%`
        );
        sectionEl.style.setProperty(
          '--produktinfo-steam-y-mobile',
          `${clampPercent(Number(steamData.yMobile), 30)}%`
        );
        sectionEl.style.setProperty(
          '--produktinfo-steam-x-desktop',
          `${clampPercent(Number(steamData.xDesktop), 50)}%`
        );
        sectionEl.style.setProperty(
          '--produktinfo-steam-y-desktop',
          `${clampPercent(Number(steamData.yDesktop), 30)}%`
        );

        steamEl.classList.toggle('produktinformationen-v2__steam--hidden', !steamData.enabled);
      }
    };

    updateVariantData();

    sectionEl.addEventListener('citydesign:variant-change', event => updateVariantData(event.detail.id));

  };

  /* ------------------------------------------------ Variantenauswahl */

  const initVariantPicker = (sectionEl) => {
    const dataEl = sectionEl.querySelector('[data-produktinformationen-variant-options]');
    const picker = sectionEl.querySelector('.produktinformationen-v2__variants');
    if (!dataEl || !picker) return;

    let variants = [];
    try {
      variants = JSON.parse(dataEl.textContent);
    } catch (error) {
      console.warn('ProduktinformationenV2: Variantenliste unlesbar.', error);
      return;
    }
    if (variants.length < 2) return;

    const labels = {
      add: sectionEl.dataset.labelAdd || '',
      soldOut: sectionEl.dataset.labelSoldOut || '',
      inStock: sectionEl.dataset.labelInStock || '',
      outOfStock: sectionEl.dataset.labelOutOfStock || ''
    };

    // Reihenfolge zaehlt: Die Optionswerte muessen in derselben Folge stehen wie
    // in variant.options. Deshalb ueber die Gruppen in DOM-Reihenfolge, nicht
    // ueber alle Selects und danach alle Radios.
    const readSelection = () =>
      [...picker.querySelectorAll('.product-form__input')]
        .map((group) => {
          const select = group.querySelector('select');
          if (select) return select.value;
          const radio = group.querySelector('input[type="radio"]:checked');
          return radio ? radio.value : null;
        })
        .filter((value) => value !== null);

    const findVariant = () => {
      const chosen = readSelection();
      return variants.find(
        (variant) =>
          variant.options.length === chosen.length &&
          variant.options.every((option, index) => option === chosen[index])
      );
    };

    const apply = () => {
      const variant = findVariant();
      if (!variant) return;

      sectionEl.querySelectorAll('input[name="id"]').forEach((input) => {
        input.value = variant.id;
        input.disabled = !variant.available;
      });

      const priceEl = sectionEl.querySelector('.produktinformationen-v2__price .price');
      if (priceEl) {
        // Frueher traf der Selektor jedes .price-item--regular — auch das im
        // Sale-Block, das den durchgestrichenen Vergleichspreis traegt. An einer
        // reduzierten Groesse stand dadurch zweimal derselbe Betrag, einmal
        // durchgestrichen. Regulaerer Preis, Aktionspreis und Vergleichspreis
        // werden deshalb einzeln gesetzt.
        const setText = (selector, text) => {
          priceEl.querySelectorAll(selector).forEach((el) => {
            el.textContent = text;
          });
        };
        setText('.price__regular .price-item--regular', variant.price);
        setText('.price__sale .price-item--sale', variant.price);
        setText('.price__sale s.price-item--regular', variant.compareAt || variant.price);
        priceEl.classList.toggle('price--on-sale', Boolean(variant.compareAt));
        priceEl.classList.toggle('price--sold-out', !variant.available);
      }

      const submit = sectionEl.querySelector(
        '.produktinformationen-v2__buy-buttons button[name="add"]'
      );
      // Der Hinweis gilt fuer die Groesse, nicht fuer ihren Lagerstand: er steht
      // auch dann, wenn sie zum Vorbestellen freigegeben ist. Im Button
      // ersetzt er nur das "Ausverkauft" — die Handlungsaufforderung bleibt.
      const prefunding = variant.prefunding || '';
      if (submit) {
        submit.disabled = !variant.available;
        const labelEl = submit.querySelector('span');
        if (labelEl) {
          labelEl.textContent = variant.available ? labels.add : prefunding || labels.soldOut;
        }
      }

      const inventoryEl = sectionEl.querySelector('.produktinformationen-v2__inventory');
      if (inventoryEl) {
        // Statt einer Lagerangabe steht bei der angekuendigten Groesse ihr
        // Hinweis: "Auf Lager" waere fuer Oktoberware falsch.
        inventoryEl.textContent = prefunding
          ? prefunding
          : variant.available
          ? labels.inStock
          : labels.outOfStock;
        inventoryEl.classList.toggle(
          'produktinformationen-v2__inventory--prefunding',
          prefunding !== ''
        );
      }

      document.querySelectorAll('[data-citydesign-shipping]').forEach(el => {
        el.textContent = variant.shipping || '';
        const item = el.closest('.citydesign-facts__item');
        if (item) item.hidden = !variant.shipping;
      });
      sectionEl.dispatchEvent(new CustomEvent('citydesign:variant-change', {detail: {id: variant.id}}));

      // Damit Neuladen, Teilen und Zurueck-Navigation die Auswahl behalten.
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    };

    picker.addEventListener('change', apply);
    apply();
  };

  const init = (sectionEl) => {
    if (sectionEl.dataset.produktinformationenReady === 'true') return;
    sectionEl.dataset.produktinformationenReady = 'true';

    initVariantMedia(sectionEl);
    initVariantPicker(sectionEl);
  };

  const initAll = () =>
    document.querySelectorAll('[data-produktinformationen-section-id]').forEach(init);

  initAll();
  document.addEventListener('shopify:section:load', initAll);
})();
